import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX } from "lucide-react";
import api from "../lib/api";
import { ArticleCard } from "../components/ArticleCard";

export default function SearchPage() {
    const [params] = useSearchParams();
    const q = params.get("q") || "";
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchPage = useCallback(async (p) => {
        const { data } = await api.get("/search", { params: { q, page: p, page_size: 12 } });
        return data;
    }, [q]);

    useEffect(() => {
        setLoading(true);
        setPage(1);
        fetchPage(1).then((data) => {
            setItems(data.items);
            setTotal(data.total);
            setHasMore(data.has_more);
        }).finally(() => setLoading(false));
    }, [q, fetchPage]);

    const loadMore = async () => {
        const next = page + 1;
        const data = await fetchPage(next);
        setItems((prev) => [...prev, ...data.items]);
        setPage(next);
        setHasMore(data.has_more);
    };

    return (
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="search-page">
            <div className="mb-10">
                <span className="eyebrow text-accent">Search results</span>
                <h1 className="font-display text-2xl sm:text-4xl font-bold mt-3 pb-3 border-b-[3px] border-accent inline-flex flex-wrap items-baseline gap-3">
                    <span>“{q}”</span>
                    <span className="font-mono text-sm font-normal text-dim tabular-nums">
                        — {total} result{total !== 1 ? "s" : ""}
                    </span>
                </h1>
            </div>

            {loading ? (
                <p className="flex items-center justify-center gap-2 font-mono text-sm text-dim py-20">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" />
                    Searching…
                </p>
            ) : items.length === 0 ? (
                <div className="panel p-14 text-center" data-testid="search-empty">
                    <span className="grid place-items-center w-12 h-12 mx-auto rounded-full bg-surface-raised text-dim">
                        <SearchX size={22} />
                    </span>
                    <p className="font-mono text-sm text-dim mt-4">No articles matched your search.</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((a) => (
                            <ArticleCard key={a.id} article={a} />
                        ))}
                    </div>
                    {hasMore && (
                        <div className="mt-14 text-center">
                            <button
                                onClick={loadMore}
                                data-testid="search-load-more"
                                className="px-9 py-3 border border-accent bg-surface text-sm font-semibold uppercase tracking-[0.07em] text-accent hover:bg-accent hover:text-accent-foreground transition-colors duration-150"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
